import React, { useState, useMemo } from "react";
import { Table as BootstrapTable } from "react-bootstrap";
import accepted from "../../assets/images/accepted.png";
import cancelled from "../../assets/images/cancelled.png";
import exit from "../../assets/images/exit.png";
import pending from "../../assets/images/pending.png";
import rejected from "../../assets/images/rejected.png";
import ReleasedAndCanceled from "../../assets/images/ReleasedAndCanceled.png";
import blocked from "../../assets/images/blocked.png";
import translate from "../../config/TranslateFile"
import { getStoredValue } from "../../_helpers/storagehelper";

const TableForContacts = ({
  columns = [],
  data = [],
  showSearch = true,
  rowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 15, 20],
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(rowsPerPage);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const currentLang = translate();
  const storedlang = getStoredValue("lang");

  const ActionIcons = {
    history: {
      className: "fas fa-history blue-color",
      title: currentLang?.Mycontacts?.Purchase_History,
    },
    transfer: {
      className: "fa-solid fa-money-bill-transfer green-color",
      title: currentLang?.Mycontacts?.Transfer_Funds,
    },
    cancel: {
      className: "fa fa-times yellow-color",
      title: currentLang?.Mycontacts?.Cancel_Request,
    },
    accept: {
      className: "fa fa-check green-color",
      title: currentLang?.Mycontacts?.Accept_Request,
    },
    credit: {
      className: " fa-solid fa-dollar-sign red-color",
      title: currentLang?.Mycontacts?.Purchase_Credit,
    },
  };

  // Filtered data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    // Normalize search term by removing extra spaces
    const normalizedSearchTerm = searchTerm.trim().replace(/\s+/g, ' ').toLowerCase();

    return data.filter((item) => {
      // Normalize each item's values and check each property separately
      const normalizedItemValues = Object.values(item).map((value) =>
        String(value)
          .trim()
          .replace(/\s+/g, ' ')  // Remove multiple spaces between words
          .toLowerCase()
      );

      // Check if the normalized search term is included in any of the individual properties
      const matchesIndividualProperties = normalizedItemValues.some((value) =>
        value.includes(normalizedSearchTerm)
      );

      // Alternatively, check if the normalized search term is included in the concatenated item string
      const normalizedItemConcatenated = normalizedItemValues.join(" ");
      const matchesConcatenatedItem = normalizedItemConcatenated.includes(normalizedSearchTerm);

      // Return true if any of the checks match
      return matchesIndividualProperties || matchesConcatenatedItem;
    });
  }, [searchTerm, data]);

  const sortedData = useMemo(() => {
    let sortableData = [...filteredData];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        let aValue, bValue;

        // Find the column definition for the current sort key
        const column = columns.find(col => col.name === sortConfig.key);

        if (column && column.selector) {
          // Use the selector to get values for sorting
          aValue = column.selector(a);
          bValue = column.selector(b);
        } else {
          // Fallback if no selector is found
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        // Perform the sorting
        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [filteredData, sortConfig, columns]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * selectedRowsPerPage;
    const endIndex = startIndex + selectedRowsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [currentPage, sortedData, selectedRowsPerPage]);


  // Total pages
  const totalPages = Math.ceil(filteredData.length / selectedRowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (e) => {
    const rows = parseInt(e.target.value, 10);
    setSelectedRowsPerPage(rows);
    setCurrentPage(1); // Reset to the first page
  };

  const handleSort = (columnKey, columnIndex) => {
    if (columnIndex >= 5) return; // Restrict sorting to the first five columns only
    setSortConfig((prevState) => {
      if (prevState.key === columnKey) {
        // If the same column is clicked, toggle the direction
        const newDirection = prevState.direction === "asc" ? "desc" : "asc";
        return { key: columnKey, direction: newDirection };
      } else {
        // Otherwise, sort by the new column in ascending order by default
        return { key: columnKey, direction: "asc" };
      }
    });
  };


  return (
    <div>
      {showSearch && (

        <div className="input-group mb-3">
          <span className="input-group-text">
            <i className="fa fa-search"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder={currentLang?.Mycontacts?.Search}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }} />
        </div>
      )}


      <div className="d-flex justify-content-end">
        <div className="icon-legend">
          <ul className="list-unstyled">
            <li className="d-flex align-items-center mb-2">
              <i className={ActionIcons.history.className} style={{ marginRight: "8px" }}></i>
              <span>{ActionIcons.history.title}</span>
            </li>
            <li className="d-flex align-items-center mb-2">
              <i className={ActionIcons.transfer.className} style={{ marginRight: "8px" }}></i>
              <span>{ActionIcons.transfer.title}</span>
            </li>
            <li className="d-flex align-items-center mb-2">
              <i className={ActionIcons.cancel.className} style={{ marginRight: "8px" }}></i>
              <span>{ActionIcons.cancel.title}</span>
            </li>
            <li className="d-flex align-items-center mb-2">
              <i className={ActionIcons.accept.className} style={{ marginRight: "8px" }}></i>
              <span>{ActionIcons.accept.title}</span>
            </li>
            <li className="d-flex align-items-center mb-2">
              <i className={ActionIcons.credit.className} style={{ marginRight: "8px" }}></i>
              <span>{ActionIcons.credit.title}</span>
            </li>
          </ul>
        </div>
      </div>


      <div className="table-responsive">
        <BootstrapTable className="table table-striped align-middle">
          <thead>
            <tr style={{ background: "#c8605e", color: "#fff" }}>
              {columns.map((col, index) => (
                <th
                  key={index}
                  style={{
                    padding: "10px",
                    textAlign: "left",
                    color: "#fff",
                    cursor: index < 5 ? "pointer" : "default", // Cursor only changes for the first five columns
                  }}
                  onClick={() => index < 5 && handleSort(col.accessor || col.name, index)} // Only allow sorting for the first five columns
                >
                  {col.name}
                  {sortConfig.key === (col.accessor || col.name) && index < 5 && (
                    <span style={{ marginLeft: "5px" }}>
                      {sortConfig.direction === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((col, colIndex) => (
                    <td
                      key={colIndex}
                      style={{
                        textAlign: col.name === "Status" ? "left" : col.name === "Action" ? "left" : "left",
                        verticalAlign: "middle",
                      }}
                    >
                      {col.name === "Action" ? (
                        <div style={{ display: "flex", justifyContent: "left" }}>
                          {typeof col.cell === "function" && col.cell(row)}
                        </div>
                      ) : typeof col.cell === "function" ? (
                        col.cell(row)
                      ) : typeof col.selector === "function" ? (
                        col.selector(row)
                      ) : (
                        row[col.accessor || col.name]
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} style={{ padding: "0" }}>
                  <div className="alert-light-red mb-3 font-size-16 p-3 text-center mt-1"
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      borderRadius: "8px",
                      margin: "20px 0",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      // minHeight: "200px"
                    }}>
                    {currentLang?.info?.NoResult}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </BootstrapTable>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-end align-items-center flex-wrap mt-3">
        {/* Pagination Controls */}
        <div className="d-flex align-items-center">
          <span>Rows per page:</span>
          <select
            className="form-select ms-2"
            style={{
              width: "70px",
              height: "36px",
              border: "1px solid #ced4da",
              borderRadius: "4px",
              paddingLeft: "8px",
            }}
            value={selectedRowsPerPage}
            onChange={handleRowsPerPageChange}
          >
            {rowsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center ms-3">
          <span style={{ marginRight: "16px" }}>
            {filteredData.length > 0
              ? `${(currentPage - 1) * selectedRowsPerPage + 1} - ${Math.min(
                currentPage * selectedRowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`
              : "0 results"}
          </span>
          <button
            className="btn"
            style={{
              color: currentPage === 1 ? "#d3d3d3" : "#000",
              fontSize: "16px",
              border: "none",
              background: "transparent",
              padding: "0 8px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
          >
            &#124;&lt;
          </button>
          <button
            className="btn"
            style={{
              color: currentPage === 1 ? "#d3d3d3" : "#000",
              fontSize: "16px",
              border: "none",
              background: "transparent",
              padding: "0 8px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            &lt;
          </button>
          <button
            className="btn"
            style={{
              color: currentPage === totalPages ? "#d3d3d3" : "#000",
              fontSize: "16px",
              border: "none",
              background: "transparent",
              padding: "0 8px",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            }}
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            &gt;
          </button>
          <button
            className="btn"
            style={{
              color: currentPage === totalPages ? "#d3d3d3" : "#000",
              fontSize: "16px",
              border: "none",
              background: "transparent",
              padding: "0 8px",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            }}
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
          >
            &gt;&#124;
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TableForContacts);
